<template>
  <div class="wrapper">
    <div id="embedded-checkout" />
    <div id="cart" class="cart-main-content desk-hor-mob-col">
      <div class="cart-gwp-section">
        <div v-if="cartInProgress" class="skeleton skeleton-section" style="height: 60px" />
        <div v-else class="flex-col mgn-btm-1-pt5">
          <div class="h2" v-text="itemsInCart ? 'Cart' : 'Your Cart Is Empty'"></div>
          <div v-if="itemsInCart">
            <span class="ff-text-500">{{ useTranslate('CartSlideout.subtotal') }}</span> ({{ numItemsCart }} item{{
              numItemsCart > 1 ? 's' : ''
            }}):
            <span class="ff-text-500" v-text="currencySymbol + formatPrice(totalPrice)"></span>
          </div>
        </div>

        <GiftWithPurchase v-if="giftWithPurchaseTest" class="cart-page" :is-cart-page="true" />

        <div class="h4 mgn-top-1-pt5">Items In Cart</div>

        <div v-if="cartInProgress">
          <div
            v-for="(item, idx) in state.latestNumLineItems"
            :key="idx + 'container'"
            class="skeleton skeleton-section item-skeleton"
          />
        </div>
        <div v-else-if="numItemsCart" class="flex-col cart-items">
          <div
            v-for="(product, index) in sortedProductsRecentlyAdded"
            :key="`${index}_${product.id}`"
            class="product-wrapper flex-inline between"
          >
            <div class="cart-item flex">
              <NuxtLink :to="localePath(product.url)">
                <CloudinaryImage
                  v-if="product.image"
                  :public-id="product.image"
                  :options="{ width: state.imgSize, height: state.imgSize, quality: 'auto', transform: 'pad' }"
                  :alt="product.title || ''"
                />
              </NuxtLink>
              <div class="flex-col">
                <div class="flex-col cart-product-title">
                  <span class="h4 product-title"
                    >{{ product.title }}
                    <div v-if="product.min > 1" class="min-quantity">(Min. {{ product.min }})</div>
                    <div v-if="product.max" class="min-quantity">(Max {{ product.max }})</div></span
                  >
                  <span v-if="product.whatsIncluded" class="variation-detail whats-included"
                    >Includes: {{ product.whatsIncluded }}</span
                  >
                  <template v-if="product.configuration && product.configuration.length">
                    <span
                      v-for="option in optConfigDisplay(product.configuration)"
                      :key="option.nameId + option.value"
                      class="variation-detail"
                    >
                      <div
                        v-if="
                          checkForEtchConfig(option.name, product.configuration.length) &&
                          checkForCustomEtchText(option.name)
                        "
                      >
                        <div v-for="(line, textIndex) in option.value.split('\n')" :key="line + textIndex">
                          Custom Text Line {{ textIndex + 1 }}: {{ line }}
                        </div>
                      </div>
                      <template v-else>
                        {{ option.name + ': ' + option.value }}
                      </template>
                    </span>
                    <div
                      v-if="checkAllConfigsForEtch(product.configuration)"
                      role="button"
                      aria-label="remove etching from cart product"
                      class="remove-button remove-etch pointer"
                      @click="removeEtching(product)"
                    >
                      Remove Etching
                    </div>
                  </template>
                  <span v-if="product.subData" class="variation-detail">{{ getFrequency(product.subData) }}</span>
                  <CartPricing :product="product" class="tab-mob-only-flex" />
                  <span v-if="product.backorderMessage" class="backorder-msg">{{ product.backorderMessage }}</span>
                </div>

                <div class="flex quantity-container mgn-top-pt5">
                  <Stepper
                    v-if="!isDiscountFree(product) && !isGiftCard(product)"
                    :counter-start="product.qty"
                    :min="0"
                    :max="product.max ? product.max + product.qty - numBaseProdInCart(product) : undefined"
                    @stepper-update="updateQuantity($event, product)"
                  />
                  <div v-else-if="!isGiftCard(product)" class="quantity-text">Quantity: {{ product.qty }}</div>
                  <div>
                    <CustomButton
                      v-if="!isDiscountFree(product)"
                      :class="'remove-button' + giftMargin(product)"
                      aria-label="Remove from Cart"
                      style-color="black"
                      style-name="tertiary"
                      @click-button="removeFromCart(product)"
                    >
                      Remove
                    </CustomButton>
                  </div>
                </div>
                <!-- <UrgencyMessaging
                  v-if="product.urgencyMessage && product.urgencyMessage.message"
                  :urgency-messaging="product.urgencyMessage"
                /> -->
              </div>
            </div>
            <CartPricing :product="product" class="desk-only-flex" />
          </div>
        </div>
        <div v-else>{{ useTranslate('CartSlideout.emptyCart') }}</div>

        <div class="page-dynamic-content flex-col desk-only">
          <PageSection
            v-for="(section, index) in sections"
            :key="`${index}-section`"
            :xxl-full-content="false"
            :section="section"
          />
        </div>
      </div>

      <div class="checkout-details flex-col">
        <BuyMoreSaveMore />
        <FreeShipping :class="['details-free-ship', { 'items-in-cart': itemsInCart }]" />
        <div class="h4">{{ useTranslate('CartSlideout.orderSummary') }}</div>
        <CheckoutDetails page-type="cart" />

        <CheckoutButton :class="{ 'checkout-sticky': itemsInCart }">
          <template v-if="itemsInCart" #buttonContent>
            <div class="tab-mob-only">
              <FreeShipping />
              <div class="flex-inline between mgn-btm-1">
                <span
                  ><span class="ff-text-500">{{ useTranslate('CartSlideout.total') }}</span> ({{ numItemsCart }} item{{
                    numItemsCart > 1 ? 's' : ''
                  }}) </span
                ><span class="ff-text-500">{{ currencySymbol + formatPrice(totalPlusTax) }}</span>
              </div>
            </div>
          </template>
        </CheckoutButton>

        <AcceptedPayments />
        <CouponCode />
        <LazyCartError v-if="cartStore.cartError" />
      </div>

      <div class="page-dynamic-content flex-col tab-mob-only">
        <PageSection
          v-for="(section, index) in sections"
          :key="`${index}-section`"
          :xxl-full-content="false"
          :section="section"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { capitalize } from 'lodash-es';
import { QueryTypes } from '~/server/utils/contentstack';
import { contentstackRequest } from '~/util/contentstack/api';
import { metaGen } from '~/util/metadata';
import { getFirstIndex } from '~/util/arrays';
import { CartObj } from '~/types/bigcommerce';
import { checkIsDiscountFree, numBaseProductsInCart } from '~/util/bigcommerce';
import { optionConfigDisplay } from '~/util/frontend';
import { getLocalStorageItem, recentlyViewedKey } from '~/util/localStorage';
import { useCartStore } from '~/store/cart';
import { useTestStore } from '~/store/test';
import { useLocaleStore } from '~/store/locale';
import { useDyStore } from '~/store/dy';
import { useRootStore } from '~/store';
// import { scrollToTop } from '~/util/eventHandler';
import { GtmEventType, mapBcCartItemsToGtmEvent } from '~/util/analytics';
import { refreshKlarna } from '~/util/klarna';

const { locale } = useI18n();
const rootStore = useRootStore();
const cartStore = useCartStore();
const testStore = useTestStore();
const localeStore = useLocaleStore();
const dyStore = useDyStore();
const localePath = useLocalePath();
const route = useRoute();
const event = useRequestEvent();

const { data: asyncData, error: asyncError } = await useAsyncData(async () => {
  const contentStackData: any = await contentstackRequest(QueryTypes.page, '/cart', {
    event,
    query: { locale: locale?.value },
  });
  if (!contentStackData) throw createError({ statusCode: 404, message: 'Page not found' });

  const page = getFirstIndex(contentStackData?.data?.all_page?.items);
  if (!page) throw createError({ statusCode: 404, message: 'Page not found' });

  return {
    contentStackData,
    page,
  };
});

useAsyncErrorHandling({
  error: asyncError.value,
});

useHead({
  ...(metaGen(rootStore?.brandSettings, {
    ...(asyncData.value?.page?.seo || {}),
  }) as any),
  script: [{ hid: 'bcSdk', src: 'https://checkout-sdk.bigcommerce.com/v1/loader.js', defer: true }],
});

const state = reactive({
  contentStackData: asyncData.value?.contentStackData || ({} as any),
  page: asyncData.value?.page || ({} as any),
  imgSize: 160,
  busEmitted: false,
  couponIsOpen: false,
  couponCode: '',
  latestNumLineItems: 2,
  recentlyViewed: null,
});

// computed - mapped
const filteredProducts = computed(() => cartStore.filteredProducts);
const numItemsCart = computed(() => cartStore.numItemsCart);
const totalPrice = computed(() => cartStore.totalPrice);
const totalPlusTax = computed(() => cartStore.totalPlusTax);
const cartActionInProgress = computed(() => cartStore.cartActionInProgress);
const gettingCart = computed(() => cartStore.gettingCart);
const products = computed(() => cartStore.products);
const giftWithPurchaseTest = computed(() => testStore.giftWithPurchaseTest);
const currencySymbol = computed(() => localeStore.currencySymbol);

// computed - defined
const sortedProductsRecentlyAdded = computed(() => {
  if (filteredProducts.value?.length) {
    const arrCopy = [...(filteredProducts.value || [])];
    return arrCopy.reverse(); // show recently added first
  }
  return filteredProducts.value;
});
const sections = computed(() => {
  return state.page?.sections || [];
});
const itemsInCart = computed(() => {
  return (numItemsCart.value || 0) > 0;
});
const cartInProgress = computed(() => {
  return cartActionInProgress.value || gettingCart.value;
});
// const discountedSavings = computed(() => {
//   if (products.value?.length) {
//     let total = 0;
//     for (let i = 0; i < products.value?.length; i++) {
//       total += products.value[i].discountAmt;
//     }
//     return total;
//   }
//   return null;
// });

// watch
watch(
  () => products.value,
  (updated, old) => {
    if (updated?.length !== old?.length && updated?.length > 0) {
      emitPageView(true);
    }
  }
);
watch(
  () => cartActionInProgress.value,
  (updated) => {
    state.latestNumLineItems = products.value?.length || 0;
    if (!updated) {
      emitPageView();
    }
  }
);
watch(
  () => gettingCart.value,
  () => {
    state.latestNumLineItems = products.value?.length || 0;
  }
);

// lifecycle
onBeforeMount(async () => {
  await recoverDraftOrder();
  document.body.classList.add('cart-page');
  if (products.value?.length > 0) {
    emitPageView();
  }
  await addProductByQuery();
  const token = route.query.token;
  if (token) {
    await nextTick();
    removeQueryParam('token');
  }
});

onMounted(() => {
  state.recentlyViewed = getLocalStorageItem(recentlyViewedKey);
  gaViewCart();
});

function removeQueryParam(param: string) {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  params.delete(param);
  const newUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
  window.history.replaceState({}, '', newUrl);
}

async function nextTickRefreshKlarna() {
  await nextTick(); // makes sure 'data-purchase-amount' on klarna html has updated in DOM
  refreshKlarna();
}

// methods - defined
function gaViewCart() {
  const gtm = useGtm();
  gtm?.trackEvent(mapBcCartItemsToGtmEvent(GtmEventType.viewCart, sortedProductsRecentlyAdded?.value));
}

function emitPageView(fromWatcher = false) {
  if (!state.busEmitted && !fromWatcher) {
    state.busEmitted = true;
    const { $bus }: any = useNuxtApp();
    $bus.emit('pageView', {
      url: document.URL,
      data: {
        products: products.value,
        totalPrice: totalPrice.value,
      },
    });
    return;
  }
  if (fromWatcher) {
    const { langCode } = useLocaleStore();
    dyStore.updateRecChoices(langCode, true);
  }
}
function checkForEtchConfig(name: string, configLength: number) {
  const ls = name?.toLowerCase();
  return (configLength > 2 && ls?.includes('college logo selection')) || ls?.includes('customer etching text');
}
function checkForCustomEtchText(name: string) {
  const ls = name?.toLowerCase();
  return ls?.includes('customer etching text');
}
function checkAllConfigsForEtch(configuration: any[]) {
  let hasEtching = false;
  configuration?.forEach((config: any) => {
    const name = config?.name?.toLowerCase() || '';
    if (name?.includes('option choice for price') || name?.includes('etching surcharge')) {
      hasEtching = true;
    }
  });
  return hasEtching;
}

const coupon = computed(() => {
  return cartStore?.coupon;
});
let couponSave = '';
async function applyCoupon(): Promise<void> {
  if (couponSave) {
    await useAddCoupons(couponSave);
    if (!cartStore.couponError) {
      await nextTickRefreshKlarna();
    }
  }
}
async function removeEtching(product: any) {
  // according to bc you need to remove/re-add the product to edit modifiers
  // skip setting true at the end of delete to avoid flicker of loading state
  couponSave = coupon?.value?.code || '';
  await useDeleteCartItem({ cartItem: { product }, skipCartActionComplete: true });
  await addEditedItemToCart(product, false);
  if (couponSave !== '') applyCoupon();
  await nextTickRefreshKlarna();
}
async function addEditedItemToCart(product: any, silentAdd?: boolean) {
  // only used for products with custom modifiers
  // need to map back to cart data structure
  const hasVariants = !!product?.variant_id;
  const cartProduct: any[] = [
    {
      quantity: product.qty,
      product: {
        id: product.id,
        image: product.image,
        variantId: product.variant_id,
      },
      hasVariants,
      ...(hasVariants && { variant: { bcId: product.variant_id } }),
      // DONT ADD OPTIONSELECTIONS (ref in mapProductAddToCart in cart.js store)
    },
  ];
  await useAddToCart({ cartItems: cartProduct, setLatestAdditions: silentAdd });
  await nextTickRefreshKlarna();
  // START - Prevents cart modal on cart page. Scrolls to top on a2c action.
  // scrollToTop();
  // cartStore.setNewItemAdded(false);
  // END
}
function numBaseProdInCart(product: any): number {
  return numBaseProductsInCart(products.value, product);
}
async function addProductByQuery(): Promise<void> {
  const { action, sku } = route?.query;
  if (!sku && !action) return;
  if (action && action === 'add') {
    await useAddByQueryParams({ sku } as any);
    await navigateTo(localePath('/cart'));
  }
}
async function recoverDraftOrder(): Promise<void> {
  try {
    const { action, quoteToken } = route?.query;
    if (action && action === 'loadSavedQuote') {
      const quote = (quoteToken || '') as string;
      const b64Arr = quote.split('.');
      // index at 1 for JWT payload
      const b64 = b64Arr[1];
      const decoded = atob(b64);
      const parsed = JSON.parse(decoded);
      const cartId = parsed?.domain?.cart?.id;
      if (!cartId) throw new Error('No draft id found in JWT');

      const shouldRedirect = await useGetDraftedCart({ cartId });
      if (shouldRedirect) {
        await navigateTo({ path: '/account', query: { action: 'loadSavedQuote' } });
      }
    }
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
  }
}
async function updateQuantity(quantity: number, product: any): Promise<void> {
  const previousQty = product.qty;
  const data: CartObj = {
    previousQty,
    quantity,
    product,
  };
  // TODO - figure out whether quantity should represent number added/removed or total quantity
  data.product.qty = quantity;
  if (quantity >= product.min) {
    await useUpdateCartItem(data, previousQty > quantity);
    await nextTickRefreshKlarna();
    gaViewCart();
  } else {
    await removeFromCart(product);
  }
}
async function removeFromCart(product: any): Promise<void> {
  await useDeleteCartItem({ cartItem: { product } });
  await nextTickRefreshKlarna();
}
function optConfigDisplay(configArr: any[]): any[] {
  return optionConfigDisplay(configArr);
}
function formatPrice(price: number): string {
  return price?.toFixed(2);
}
function isDiscountFree(product: any) {
  return checkIsDiscountFree(product);
}
function isGiftCard(product: any) {
  return product.sku === 'SS-GIFT-CARD';
}
function giftMargin(product: any) {
  if (isGiftCard(product)) return ' remove-button--gift-card';
  else return '';
}
// async function removeUrlToken() {
//   const token = route.query.token || route.query.quoteToken;
//   if (token && token !== '') {
//     await navigateTo(localePath('/cart'));
//   }
// }
function getFrequency(prefs: any): string {
  const interval = capitalize(prefs?.subscription_preferences?.interval_unit || '');
  const frequency = prefs?.subscription_preferences?.order_interval_frequency;
  return interval && frequency ? `Delivered Every ${frequency} ${interval}(s)` : '';
}
</script>

<style lang="scss">
.cart-main-content .yotpo.bottomLine {
  .rating-star {
    font-size: 0.875rem;
  }
  .text-m {
    font-size: 0.75rem;
  }
}
</style>

<style lang="scss" scoped>
.cart-main-content {
  @include local-mixins.desktop {
    gap: 40px;
  }
  @include local-mixins.xxl {
    max-width: local-vars.$xl-content-max-width;
    margin: 0 auto;
  }
  @include local-mixins.tablet_and_mobile {
    margin-top: 1rem;
  }
}
.cart-gwp-section {
  @include local-mixins.desktop {
    min-width: 66%;
  }
}
.wrapper {
  width: 100%;
  margin: 0 auto;
  margin-bottom: 40px;
  .gwp-container {
    border-bottom: 1px solid $color-neutral-cool-200;
  }
  @include local-mixins.desktop {
    max-width: calc(88.75rem); // 1452px - 32px for padding
  }
  @include local-mixins.xxl {
    max-width: calc(78rem); // 1280px - 32px for padding
  }
}
.page-dynamic-content {
  margin-top: 3rem;
  @include local-mixins.tablet_and_mobile {
    margin-top: 2.5rem;
  }
}
.cart-items {
  width: 100%;
  .cart-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    @include local-mixins.tablet_and_mobile {
      align-items: flex-start;
    }
  }
}
.account-value-prop {
  line-height: 1.5;
}
.backorder-msg {
  color: $color-primary-600;
}
.variation-detail,
.backorder-msg,
.quantity-text {
  font-size: 0.875rem;
  line-height: 1.5;
  &.whats-included {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }
}
.quantity-container {
  align-items: center;
}
.min-quantity {
  font-size: 0.6875rem;
  font-weight: normal;
}
.checkout-details {
  width: 100%;
  :deep(.checkout-btn-wrapper) {
    width: 100%;
    .btn {
      width: 100%;
    }
  }
  .checkout-btn-wrapper {
    margin-top: 1rem;
  }
  @include local-mixins.tablet_and_mobile {
    margin-top: 1.5rem;
    .details-free-ship.items-in-cart {
      display: none;
    }
  }
  @include local-mixins.desktop {
    position: sticky;
    top: 181px;
    height: 100%;
  }
}
.cart-product-title {
  color: $color-neutral-cool-900;
  .product-title {
    font-size: 1.125rem;
  }
  @include local-mixins.tablet_and_mobile {
    margin-left: 0;
    .product-title {
      line-height: 1;
    }
  }
}
.checkout-sticky {
  @include local-mixins.tablet_and_mobile {
    z-index: map.get(local-vars.$zindex, 'dropdown');
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 0.5rem 1rem 1rem 1rem;
    background-color: $color-neutral-white;
    border-top: 1px solid $color-neutral-cool-200;
    &:deep(.checkout-btn-wrapper) .btn {
      padding: 0.75rem 1.5rem;
    }
  }
}
.remove-button {
  font-size: 0.875rem;
  align-self: flex-start;
  margin-left: 1.5rem;
  width: auto;
  text-transform: capitalize;
  text-decoration: underline;
  font-weight: 400;
  &.remove-etch {
    color: $color-primary-600;
    margin-left: 0;
    line-height: 1.5;
  }
}
:deep(.section-wrapper) .scroll-wrapper .dy-card-container {
  min-width: 200px;
  .product-card {
    .image {
      width: 200px;
      height: 200px;
    }
    .product-title {
      font-size: 1rem;
    }
    .current-price,
    .orig-price .strikethrough {
      font-size: 1rem;
    }
  }
}
:deep(.section-wrapper) {
  .content-block {
    max-width: unset;
    margin: 0;
  }
}

.product-wrapper {
  padding: 1.5rem 0;
  border-bottom: 1px solid $color-neutral-cool-200;
  img {
    width: 160px;
    height: auto;
    border-radius: 4px;
  }
  @include local-mixins.tablet_and_mobile {
    img {
      width: 80px;
    }
  }
}
.item-skeleton {
  height: 145px;
  @include local-mixins.desktop {
    height: 200px;
  }
}

.add-to-cart-error {
  text-align: right;
}

.highlight {
  color: $color-primary-600;
}
.remove-button--gift-card {
  margin-left: 0;
}
</style>
